import { useCallback, useEffect, useRef, useState } from "react";
import {
  createSportsbookHistoryCancelCommand,
  getSportsbookHistoryErrorMessage,
  isStaleSportsbookHistoryCommandError,
  requestSportsbookHistoryCancel,
  requestSportsbookHistoryDetail,
} from "../api/sportsbookHistoryRequest.api.js";
import {
  assertSportsbookHistoryCancelAcknowledgement,
  waitForSportsbookHistoryCancellation,
} from "./sportsbookHistoryCancelProjection.command.js";

const WAITING_NOTICE = "취소 요청은 처리됐습니다. 내역 반영을 확인하고 있습니다.";
const PENDING_NOTICE = "취소 요청은 처리됐습니다. 내역 반영을 확인하지 못했습니다. 내역 다시 확인을 눌러 주세요.";

export const useSportsbookHistoryCancel = ({ service, onRefresh, onReconciled }) => {
  const [cancelingHistoryId, setCancelingHistoryId] = useState("");
  const [pendingHistoryId, setPendingHistoryId] = useState("");
  const [projectionNotice, setProjectionNotice] = useState("");
  const [rechecking, setRechecking] = useState(false);
  const requestRef = useRef({ controller: null, generation: 0, pendingRow: null });
  const mountedRef = useRef(true);
  const currentRef = useRef({ service, onRefresh, onReconciled });
  currentRef.current = { service, onRefresh, onReconciled };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestRef.current.generation += 1;
      requestRef.current.controller?.abort();
    };
  }, []);

  const reconcile = useCallback(async (row, controller, generation, requestService) => {
    const isCurrent = () => mountedRef.current && !controller.signal.aborted
      && requestRef.current.generation === generation;
    if (!isCurrent()) return null;
    setProjectionNotice(WAITING_NOTICE);
    setRechecking(true);
    try {
      const result = await waitForSportsbookHistoryCancellation({
        row,
        signal: controller.signal,
        requestDetail: (historyId, signal) => requestSportsbookHistoryDetail({
          service: requestService, historyId, signal,
        }),
      });
      if (!isCurrent()) return null;
      if (result.status === "confirmed") {
        currentRef.current.onReconciled?.(result.detail);
        requestRef.current.pendingRow = null;
        setPendingHistoryId("");
        setProjectionNotice("");
        return { ok: true, error: "", projectionPending: false };
      }
      setProjectionNotice(PENDING_NOTICE);
      return { ok: true, error: "", projectionPending: true };
    } catch {
      if (!isCurrent()) return null;
      setProjectionNotice(PENDING_NOTICE);
      return { ok: true, error: "", projectionPending: true };
    } finally {
      if (isCurrent()) setRechecking(false);
    }
  }, []);

  const cancel = useCallback(async (row) => {
    if (row?.capabilities?.can_cancel !== true) return null;
    if (!mountedRef.current || requestRef.current.controller || requestRef.current.pendingRow) return null;
    const controller = new AbortController();
    const generation = requestRef.current.generation + 1;
    requestRef.current = { controller, generation, pendingRow: null };
    setCancelingHistoryId(row.history_id);
    const isCurrent = () => mountedRef.current && !controller.signal.aborted
      && requestRef.current.generation === generation;
    try {
      const requestService = currentRef.current.service;
      const command = createSportsbookHistoryCancelCommand(row.row_version);
      const acknowledgement = await requestSportsbookHistoryCancel({
        service: requestService, historyId: row.history_id, command, signal: controller.signal,
      });
      if (!isCurrent()) return null;
      assertSportsbookHistoryCancelAcknowledgement(acknowledgement, row, command);
      requestRef.current.pendingRow = row;
      setPendingHistoryId(row.history_id);
      return await reconcile(row, controller, generation, requestService);
    } catch (error) {
      if (!isCurrent() || error?.name === "AbortError") return null;
      if (isStaleSportsbookHistoryCommandError(error)) currentRef.current.onRefresh?.(row);
      return { ok: false, error: getSportsbookHistoryErrorMessage(error, "취소 실패") };
    } finally {
      if (isCurrent()) {
        requestRef.current.controller = null;
        setCancelingHistoryId("");
      }
    }
  }, [reconcile]);

  const recheck = useCallback(async () => {
    const row = requestRef.current.pendingRow;
    if (!mountedRef.current || !row || requestRef.current.controller) return null;
    const controller = new AbortController();
    const generation = requestRef.current.generation + 1;
    requestRef.current = { controller, generation, pendingRow: row };
    try {
      return await reconcile(row, controller, generation, currentRef.current.service);
    } finally {
      if (mountedRef.current && requestRef.current.generation === generation && !controller.signal.aborted) {
        requestRef.current.controller = null;
      }
    }
  }, [reconcile]);

  return { cancel, cancelingHistoryId, pendingHistoryId, projectionNotice, rechecking, recheck };
};
